import React, { useEffect, useState } from "react";
import { useWatch, useForm, SubmitHandler } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@src/components/Button";
import item from "./item.module.scss";
import InputPhone from "@src/components/InputPhone";
import toast from "react-hot-toast";
// import { useMonnifyPayment } from "react-monnify";
import { usePaystackPayment } from "react-paystack";
import { useMutation, useQuery } from "@apollo/client";
import {
  Currency,
  EventSupportCurrencyProvider,
  GenerateGiftPaymentMetadataMutation,
  GenerateGiftPaymentMetadataMutationVariables,
  GiftPaymentMetadataRequest,
  PaymentProcessor,
  UserQuery,
  UserQueryVariables,
  VerifyTransactionMutation,
  VerifyTransactionMutationVariables,
} from "@src/@types/api.d";
import {
  GenerateGiftPaymentMetadata,
  VerifyTransaction,
} from "@src/graphql/mutations";
import Icon from "@src/components/Icon";
import { PaystackProps } from "react-paystack/dist/types";
import { GetProfileDetails } from "@src/graphql/queries";
import { useFlutterwave } from "flutterwave-react-v3";
import { FlutterwaveConfig } from "flutterwave-react-v3/dist/types";
import getSymbolFromCurrency from "currency-symbol-map";
import useStore from "@src/store";

interface Values {
  fullName: string;
  email: string;
  amount: number | string;
}

const GeneralFormSchema = Yup.object().shape({
  fullName: Yup.string().required("Full name is required"),
  email: Yup.string().email().required("Email is required"),
  amount: Yup.string().required("Amount is required"),
});

interface ItemProps {
  title: string;
  id: string;
  amount: number;
  eventId: number;
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenItem: React.Dispatch<React.SetStateAction<boolean>>;
  currencyToPay: Currency;
  provider: EventSupportCurrencyProvider | null;
}

const Item = ({
  title,
  id,
  eventId,
  eventUuid,
  amount,
  setOpen,
  setOpenItem,
  currencyToPay,
  provider,
}: ItemProps) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const { data: profile } = useQuery<UserQuery, UserQueryVariables>(
    GetProfileDetails,
    {
      onError(error) {
        if (error.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occured"}</p>
          );
        }
      },
    }
  );

  const [defaultValues, setDefaultValues] = useState<Values>({
    fullName: "",
    email: "",
    amount: `${amount}`,
  });

  const [flutterwaveConfig, setFlutterwaveConfig] =
    useState<FlutterwaveConfig | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<Values>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(GeneralFormSchema),
  });

  useEffect(() => {
    if (profile?.user) {
      const newDefaultValues = {
        fullName: `${profile.user.first_name || ""} ${
          profile.user.last_name || ""
        }`.trim(),
        email: profile.user.email || "",
        amount: `${amount}`,
      };
      setDefaultValues({ ...defaultValues, ...newDefaultValues });

      reset(newDefaultValues); // Reset form with new default values
    }
  }, [profile, reset]);

  const amountToPay = useWatch({ control, name: "amount" });
  const [phoneNumberValue, setPhoneNumberValue] = React.useState<string>("");

  useEffect(() => {
    setPhoneNumberValue(profile?.user?.phone_number || "");
  }, [profile?.user?.phone_number]);

  const [verifyTransaction] = useMutation<
    VerifyTransactionMutation,
    VerifyTransactionMutationVariables
  >(VerifyTransaction, {
    onError(error) {
      // console.log(error);
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  // Mutation for generating payment metadata
  const [generateGiftMetadataFn] = useMutation<
    GenerateGiftPaymentMetadataMutation,
    GenerateGiftPaymentMetadataMutationVariables
  >(GenerateGiftPaymentMetadata, {
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  const [paystackConfig, setPaystackConfig] = useState<PaystackProps | null>(
    null
  );

  const onSuccessWithPaystack = () => {
    verifyTransaction({
      variables: {
        reference: paystackConfig?.reference || "",
        provider: PaymentProcessor.Paystack,
      },
    });
    toast.success(<p className="toast">Payment successful</p>);
  };

  const paystackCharge = React.useMemo(() => {
    const preTotal = parseInt(`${amountToPay}`);
    const percentCharge = 0.015 * preTotal;
    const fullCharge = percentCharge + 100;
    if (preTotal < 2500) {
      return { charge: percentCharge };
    } else if (fullCharge > 2000) {
      return { charge: 2000, with100: true };
    } else {
      return { charge: fullCharge, with100: true };
    }
  }, [amountToPay]);

  const finalAmount = React.useMemo(() => {
    const paystackFeeRate = 0.015;
    let paystackFixedFee = 0;
    const feeCap = 2000;
    if (Number(amountToPay) >= 2500) {
      paystackFixedFee = 100.0;
    }
    const applicableFee =
      paystackFeeRate * Number(amountToPay) + paystackFixedFee;
    const final =
      applicableFee > feeCap
        ? Number(amountToPay) + feeCap
        : (Number(amountToPay) + paystackFixedFee) / (1 - paystackFeeRate) +
          0.01;
    const res = parseFloat(final.toFixed(2));
    return res;
  }, [amountToPay]);

  // let monnifyConfigData = {
  //   apiKey: process.env.NEXT_PUBLIC_MONNIFY_API_ENV!,
  //   reference: new Date().getTime().toString(),
  //   amount: Number(amountToPay) || amount,
  //   currency: "NGN",
  //   paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
  //   customerFullName: fullName,
  //   customerEmail: email,
  //   contractCode: process.env.NEXT_PUBLIC_MONNIFY_CONTRACT_CODE!,
  //   customerMobileNumber: "phoneNumberValue",
  //   isTestMode: true,
  //   metadata: {
  //     title: "Wishlist Item",
  //     variable_name: "wishlist_item",
  //     user_id: userId,
  //     event_id: eventId,
  //     trans_type: "credit",
  //     description: "paid for wishlist",
  //     item_id: id,
  //     item_name: title,
  //     logo: "",
  //   },
  // };

  // let initializePaymentWithMonnify = useMonnifyPayment(monnifyConfigData);

  // const onSuccessWithMonnify = (reference: any) => {
  //   if (reference.paymentStatus === "PAID") {
  //     PayForWishlistFn({
  //       variables: {
  //         payload: {
  //           currency: Currency.ngn,
  //           itemId: `${id}`,
  //           event_id: eventId,
  //           amount: Number(amountToPay) || amount,
  //           status: true,
  //           itemName: title,
  //           full_name: fullName,
  //           phone: phoneNumberValue,
  //           email: email,
  //           reference: reference.transactionReference as string,
  //           paymentProcessor: paymentProcessor.monnify,
  //           creditorAlias: fullName,
  //         },
  //       },
  //     });
  //     toast.success(<p className="toast">Payment successful</p>);
  //   }
  // };

  // const payWithMonnify: SubmitHandler<Values> = () => {
  //   setOpen(false);
  //   setOpenItem(false);
  //   initializePaymentWithMonnify(onSuccessWithMonnify);
  // };

  const payWithPaystack: SubmitHandler<Values> = async (data) => {
    if (!eventUuid) {
      toast.error("Event ID is missing");
      return;
    }

    const input: GiftPaymentMetadataRequest = {
      amount: Number(data.amount) * 100,
      contact_email: data.email,
      contact_phone: phoneNumberValue,
      currency: Currency.Ngn,
      currency_iso: Currency.Ngn,
      event_id: eventUuid ?? "",
      full_name: data.fullName,
      note: `${title} payment (Wishlist item)`,
      payment_provider: PaymentProcessor.Paystack,
      // user_id: profile?.user?.uuid ?? "",
      wish_list_item_id: id,
    };

    try {
      // Call mutation to generate payment metadata
      const { data: mutationData } = await generateGiftMetadataFn({
        variables: {
          input,
        },
      });

      const paymentMeta = mutationData?.generateGiftPaymentMetadata;

      setPaystackConfig({
        reference: paymentMeta?.payment_reference || "",
        email: paymentMeta?.contact_email || "",
        amount: Number(data.amount) * 100,
        publicKey: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY || "",
        metadata: paymentMeta,
        bearer: "subaccount",
        subaccount: paymentMeta?.sub_account_code,
        transaction_charge: Math.round(
          ((paymentMeta?.split_percentage ?? 0) / 100) *
            (Number(data.amount) * 100) || 0
        ),
      });
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };

  const payWithFlutterwave: SubmitHandler<Values> = async (data) => {
    if (!eventUuid) {
      toast.error("Event ID is missing");
      return;
    }

    const input: GiftPaymentMetadataRequest = {
      amount: Number(data.amount),
      contact_email: data.email,
      contact_phone: phoneNumberValue,
      currency: currencyToPay?.toLowerCase() as Currency,
      currency_iso: currencyToPay?.toLowerCase() as Currency,
      event_id: `${eventUuid}`,
      full_name: data.fullName,
      note: `${title} payment (Wishlist item)`,
      payment_provider: PaymentProcessor.Flutterwave,
      // user_id: profile?.user?.uuid ?? "",
      wish_list_item_id: id,
    };

    try {
      // Call mutation to generate payment metadata
      const { data: mutationData } = await generateGiftMetadataFn({
        variables: {
          input,
        },
      });

      const paymentMeta = mutationData?.generateGiftPaymentMetadata;

      setFlutterwaveConfig({
        tx_ref: paymentMeta?.payment_reference || "",
        amount: Number(amount),
        public_key: process.env.NEXT_PUBLIC_FLUTTERWAVE_PUBLIC_KEY || "",
        currency: currencyToPay?.toLowerCase() as Currency,
        // meta: paymentMeta,
        meta: {
          inawo_product: `${paymentMeta?.inawo_product}`,
          event_id: `${paymentMeta?.event_id}`,
        },
        payment_options: "card,mobilemoney,ussd,banktransfer",
        customer: {
          email: paymentMeta?.contact_email || "",
          phone_number: phoneNumberValue,
          name: paymentMeta?.full_name || "",
        },
        customizations: {
          title: paymentMeta?.inawo_product || "",
          description: `Cash gift payment for ${paymentMeta?.inawo_product}`,
          logo: "",
        },
        subaccounts: [
          {
            id: `${paymentMeta?.sub_account_code}`,
            transaction_charge_type: "percentage",
            transaction_charge: (paymentMeta?.split_percentage ?? 0) / 100,
          },
        ],
      });
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };

  // Always call the hook unconditionally with a default config
  const initializePaymentWithPaystack = usePaystackPayment(
    paystackConfig || { reference: "", email: "", amount: 0, publicKey: "" }
  );

  const initializeFlutterWavePayment = useFlutterwave(
    flutterwaveConfig || {
      public_key: "",
      tx_ref: "",
      amount: 0,
      customer: {
        email: "",
        phone_number: "",
        name: "",
      },
      payment_options: "",
      customizations: {
        title: "",
        description: "",
        logo: "",
      },
    }
  );

  useEffect(() => {
    if (paystackConfig) {
      setOpen(false);
      setOpenItem(false);
      initializePaymentWithPaystack(onSuccessWithPaystack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paystackConfig]);

  useEffect(() => {
    if (flutterwaveConfig) {
      setOpen(false);
      initializeFlutterWavePayment({
        callback: () => {
          console.log("callback");
          // verifyTransactionFn({
          //   variables: {
          //     reference: flutterwaveConfig?.tx_ref || "",
          //     provider: paymentProcessor.flutterwave,
          //   },
          // });
        },
        onClose: () => {
          console.log("onClose");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flutterwaveConfig]);

  return (
    <div className={item["item--content"]}>
      <div className={item["item--content__top"]}>
        <h1>
          {title} ({currencyToPay?.toUpperCase()})
        </h1>
      </div>

      {isPreviewMode && (
        <p className="text-sm text-center text-[#71717A] mb-4">
          You are in preview mode.
        </p>
      )}

      <form className={item["item--content-form"]}>
        <div className={item["item--content-form_container"]}>
          <div className={item["input"]}>
            <label htmlFor="fullName">Full Name</label>
            <input
              {...register("fullName")}
              type="text"
              name="fullName"
              placeholder="e.g Steve Bakery"
              className={item["item--content-form__field"]}
            />
          </div>
          <p className={item["item--content-form__errors"]}>
            {errors.fullName?.message} &nbsp;
          </p>
          <div className={item["input"]}>
            <label htmlFor="email">Email</label>
            <input
              {...register("email")}
              type="email"
              className={item["item--content-form__field"]}
              name="email"
              placeholder="timi@gmail.com"
            />
          </div>
          <p className={item["item--content-form__errors"]}>
            {errors.email?.message}&nbsp;
          </p>
          <InputPhone
            name="phoneNumber"
            label="Phone Number"
            placeholder="Enter Phone Number (Whatsapp Number)"
            value={phoneNumberValue}
            setValue={setPhoneNumberValue}
          />
          <p className={item["item--content-form__errors"]}>&nbsp;</p>
          <div className={item["input"]}>
            <label htmlFor="amount">Amount</label>
            <input
              {...register("amount")}
              type="number"
              name="amount"
              className={item["item--content-form__field"]}
              placeholder="100000"
            />
            <div className={item["item--content-form__note"]}>
              <span>
                {amount
                  ? paystackCharge?.with100
                    ? "+1.5% + ₦100 processing fee"
                    : "+1.5% processing fee"
                  : ""}
              </span>
            </div>
          </div>
          <p className={item["item--content-form__errors"]}>
            {errors.amount?.message} &nbsp;
          </p>
        </div>
        <div className={item["item--content-btnContainer"]}>
          <Button
            type="button"
            design="secondary"
            label={
              provider === EventSupportCurrencyProvider.Paystack
                ? "Pay via Paystack"
                : "Pay via Flutterwave"
            }
            handleClick={
              provider === EventSupportCurrencyProvider.Paystack
                ? handleSubmit(payWithPaystack)
                : handleSubmit(payWithFlutterwave)
            }
            disabled={phoneNumberValue === "" || isSubmitting || !isValid}
          >
            <div>
              {provider === EventSupportCurrencyProvider.Paystack ? (
                <Icon iconName="paystack" />
              ) : provider === EventSupportCurrencyProvider.Flutterwave ? (
                <Icon iconName="flutterwave" />
              ) : null}
              <p className={item["item--content-btnContainer-p"]}>
                {phoneNumberValue === "" || !isValid
                  ? `Pay ${
                      provider === EventSupportCurrencyProvider.Paystack
                        ? "via Paystack"
                        : provider === EventSupportCurrencyProvider.Flutterwave
                        ? "via Flutterwave"
                        : ""
                    }`
                  : `Pay ${getSymbolFromCurrency(
                      currencyToPay
                    )} ${finalAmount}`}
              </p>
            </div>
          </Button>
          {/* <Button
            type="button"
            design="secondary"
            label="Pay via Monnify"
            handleClick={handleSubmit(payWithMonnify)}
            disabled={phoneNumberValue === "" || isSubmitting || !isValid}
          >
            <div>
              <Icon iconName="monnify" />
              <p className={item["item--content-btnContainer-p"]}>
                Pay via Monnify
              </p>
            </div>
          </Button> */}
        </div>
      </form>
    </div>
  );
};

export default Item;
